import React, { useEffect, useState } from 'react';
import { X, BatteryWarning, Wrench } from 'lucide-react';
import InspectorPrehled from './InspectorPrehled';
import InspectorJizdy from './InspectorJizdy';

// Práh palubního napětí: pod 12.0 V je 12V baterie auta slabá (klidové
// zdravé napětí je 12.4–12.7 V; při běžícím motoru by měl alternátor držet
// 13.5–14.5 V). Hranice 12.0 V slouží jako binární OK/špatně signál.
const BATTERY_OK_V = 12.0;

const SECTION_COLOR = '#0CC0DF';

// Na Živě jen to, co se týká jízdy. Tankování, servis, pojištění a
// nastavení auta jsou v detailu v záložce Auta (odkaz v hlavičce).
const SUB_TABS = [
    { id: 'prehled',    label: 'Přehled' },
    { id: 'jizdy',      label: 'Kniha jízd' },
];

export default function Inspector({ vozidlo, ridic, onClose, onDetailAuta = null, isDark, selectedJizdaId = null, onSelectJizda, playback = null }) {
    const [subtab, setSubtab] = useState('prehled');

    useEffect(() => { setSubtab('prehled'); }, [vozidlo?.id]);

    if (!vozidlo) return null;

    const labelColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const subtle = isDark ? 'rgba(255,255,255,0.65)' : 'var(--text-secondary)';
    const textColor = isDark ? '#fff' : 'var(--text-primary)';
    const headerBg = isDark
        ? 'linear-gradient(135deg, rgba(12,192,223,0.10), rgba(12,192,223,0.02))'
        : 'linear-gradient(135deg, rgba(12,192,223,0.08), rgba(12,192,223,0.02))';
    const subtabBorderActive = SECTION_COLOR;
    const closeHover = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';

    // Bez vlastního cardStyle/borderRadius — Inspector je obvykle vsazený do
    // SidePanel kontejneru, který poskytuje pozadí, border a stín. Ponecháváme
    // jen vnitřní layout (header / sub-tabs / body).
    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
        }}>
            <div style={{
                background: headerBg,
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
                padding: '14px 18px',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 12,
            }}>
                <div style={{ minWidth: 0 }}>
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        fontSize: 20, fontWeight: 800,
                        letterSpacing: 1,
                        fontVariantNumeric: 'tabular-nums',
                        color: textColor,
                    }}>
                        {vozidlo.spz}
                        {(vozidlo.stav === 'servis' || vozidlo.aktivni_servis) && (
                            <Wrench size={16} style={{ color: '#FF8A00' }} aria-label="V servisu" />
                        )}
                    </div>
                    <div style={{ fontSize: 12, color: subtle, marginTop: 2 }}>
                        {vozidlo.model}{vozidlo.rok_vyroby ? ` · ${vozidlo.rok_vyroby}` : ''}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        {vozidlo.aktualni_ridic_id && (
                            <span style={{
                                padding: '3px 8px', borderRadius: 5,
                                fontSize: 10, fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: 0.4,
                                background: 'rgba(16,185,129,0.12)',
                                color: '#10B981',
                                border: '1px solid rgba(16,185,129,0.30)',
                            }}>
                                Aktivní jízda
                            </span>
                        )}
                        {vozidlo.zaznameno && (
                            <span style={{
                                padding: '3px 8px', borderRadius: 5,
                                fontSize: 10, fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: 0.4,
                                background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                                color: subtle,
                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                                fontVariantNumeric: 'tabular-nums',
                            }}>
                                GPS · {new Date(vozidlo.zaznameno).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        )}
                        {vozidlo.power_v !== null && vozidlo.power_v !== undefined && Number(vozidlo.power_v) < BATTERY_OK_V && (() => {
                            const v = Number(vozidlo.power_v);
                            return (
                                <span
                                    title={`Palubní napětí ${v.toFixed(2)} V`}
                                    style={{
                                        padding: '3px 8px', borderRadius: 5,
                                        fontSize: 10, fontWeight: 700,
                                        textTransform: 'uppercase', letterSpacing: 0.4,
                                        background: 'rgba(239,68,68,0.12)',
                                        color: '#EF4444',
                                        border: '1px solid rgba(239,68,68,0.40)',
                                        display: 'inline-flex', alignItems: 'center', gap: 4,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    <BatteryWarning size={12} /> {v.toFixed(1)} V
                                </span>
                            );
                        })()}
                    </div>
                </div>
                {onDetailAuta && (
                    <button type="button" onClick={onDetailAuta}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: SECTION_COLOR, fontSize: 12, fontWeight: 600, padding: '6px 4px', whiteSpace: 'nowrap', marginLeft: 'auto' }}>
                        Detail auta ›
                    </button>
                )}
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Zavřít inspector"
                    style={{
                        background: 'transparent',
                        border: 'none',
                        color: labelColor,
                        cursor: 'pointer',
                        padding: 6,
                        borderRadius: 6,
                        display: 'flex',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = closeHover; e.currentTarget.style.color = textColor; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = labelColor; }}
                >
                    <X size={18} />
                </button>
            </div>

            <div style={{
                display: 'flex', gap: 4,
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
                padding: '0 12px',
                overflowX: 'auto',
            }}>
                {SUB_TABS.map((t) => {
                    const active = subtab === t.id;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setSubtab(t.id)}
                            style={{
                                background: 'none', border: 'none',
                                color: active ? SECTION_COLOR : subtle,
                                cursor: 'pointer',
                                padding: '10px 12px',
                                fontSize: 12, fontWeight: 600,
                                borderBottom: `2px solid ${active ? subtabBorderActive : 'transparent'}`,
                                marginBottom: -1,
                                whiteSpace: 'nowrap',
                                transition: 'color 0.15s',
                            }}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>

            <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1 }}>
                {subtab === 'prehled' && <InspectorPrehled vozidlo={vozidlo} ridic={ridic} isDark={isDark} />}
                {subtab === 'jizdy' && (
                    <InspectorJizdy
                        vozidlo={vozidlo}
                        selectedJizdaId={selectedJizdaId}
                        onSelectJizda={onSelectJizda}
                        isDark={isDark}
                        playback={playback}
                    />
                )}
            </div>
        </div>
    );
}
